<template>
  <b-modal id="bv-modal-download-album-choice"
           size="md"
           centered
           hide-header
           :hide-footer="loading"
           @show="onShow">



      <div class="text-center">
        <Icon name="grDownload" :width="36" class="mb-3"/>
        <h1 class="modal-title mb-2">{{ $t('modal_download_album_choice.title') }}</h1>
      </div>

     <div v-if="loading" class="text-center py-3">
      <b-spinner small></b-spinner>
    </div>
      <div v-else class="mt-4">
        <p class="mb-2">{{ $t('modal_download_album_choice.choose_galleries') }}</p>
        <b-form-group class="mb-0">
          <b-form-checkbox
              v-for="item in flatTreeItems"
              :key="item.id"
              :checked="isItemFullySelected(item)"
              :indeterminate="isItemIndeterminate(item)"
              @change="checked => toggleItem(item, checked)"
              :style="{ marginLeft: (item.depth * 1.5) + 'rem' }"
              class="mb-2">
            {{ item.name }}
            <span v-if="item.count != null" class="text-secondary ml-1">({{ item.count }})</span>
          </b-form-checkbox>
        </b-form-group>
      </div>


    <template #modal-footer="{ cancel }">
      <div class="w-100">
        <b-button variant="primary" block @click="downloadEmail"
                  :disabled="loading || downloadDisabled" class="mb-2">
          <Icon name="send" :width="18" class="mr-1" vertical-align="sub"/>
          {{ $t('modal_download_album_choice.action_email') }}
        </b-button>
        <b-button variant="link" block @click="cancel()" class="text-secondary">
          {{ $t('modal_download_album_choice.action_cancel') }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import EventService from "@/services/Event";
import mediaService from "@/domain/galleries/service";
import galleryTreeService from "@/domain/galleryTree/service";
import {galleryTypes} from "@/domain/galleries/constants";

export default {
  name: "ModalDownloadAlbumChoice",


  props: {
    checkin: {
      type: Object,
      default: null,
    },
    galleryId: {
      type: Number,
      default: null,
    },
    galleries: {
      type: Array,
      default: null,
    },
    fetchGalleries: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
      selectedGalleryIds: [],
      treeNodes: [],
    }
  },

  computed: {
    effectiveCheckin() {
      return this.checkin || this.$store.getters['checkinDetail/checkin']
    },
    flatTreeItems() {
      const items = []
      const walk = (nodes, depth) => {
        for (const node of nodes) {
          items.push({
            id: node.id,
            name: node.name,
            count: node.count,
            depth,
            leafIds: this.collectLeafIds(node),
          })
          if (node.children && node.children.length > 0) {
            walk(node.children, depth + 1)
          }
        }
      }
      walk(this.treeNodes, 0)
      return items
    },
    allLeafIds() {
      const ids = []
      const walk = (nodes) => {
        for (const node of nodes) {
          if (!node.children || node.children.length === 0) ids.push(node.id)
          else walk(node.children)
        }
      }
      walk(this.treeNodes)
      return ids
    },
    showCheckboxList() {
      return this.allLeafIds.length > 1
    },
    downloadDisabled() {
      return this.selectedGalleryIds.length === 0
    },
  },

  methods: {
    collectLeafIds(node) {
      if (!node.children || node.children.length === 0) return [node.id]
      const ids = []
      for (const child of node.children) {
        ids.push(...this.collectLeafIds(child))
      }
      return ids
    },
    isItemFullySelected(item) {
      return item.leafIds.length > 0 && item.leafIds.every(id => this.selectedGalleryIds.includes(id))
    },
    isItemIndeterminate(item) {
      if (item.leafIds.length <= 1) return false
      const selectedCount = item.leafIds.filter(id => this.selectedGalleryIds.includes(id)).length
      return selectedCount > 0 && selectedCount < item.leafIds.length
    },
    toggleItem(item, checked) {
      const ids = new Set(this.selectedGalleryIds)
      if (checked) item.leafIds.forEach(id => ids.add(id))
      else item.leafIds.forEach(id => ids.delete(id))
      this.selectedGalleryIds = [...ids]
    },
    async onShow() {
      this.treeNodes = []
      this.selectedGalleryIds = []
      if (this.effectiveCheckin) {
        try {
          this.loading = true
          this.treeNodes = this.effectiveCheckin.use_gallery_tree
              ? await this.fetchTreeGalleries()
              : await this.fetchFlatGalleries()
        } catch (_) {
          this.treeNodes = []
        } finally {
          this.loading = false
        }
      } else if (Array.isArray(this.galleries)) {
        this.treeNodes = this.buildFlatNodes(this.galleries)
      }
      this.selectedGalleryIds = this.allLeafIds
    },
    buildFlatNodes(list) {
      const seen = new Set()
      const nodes = []
      for (const g of list) {
        if (!g || g.id == null || seen.has(g.id)) continue
        seen.add(g.id)
        const count = g.count != null ? g.count
            : g.media_count != null ? g.media_count
                : g.num_media != null ? g.num_media
                    : null
        nodes.push({id: g.id, name: g.name, count, children: null})
      }
      return nodes
    },
    async fetchTreeGalleries() {
      const rootId = this.galleryId != null ? this.galleryId : 0
      return await this.buildSubtree(this.effectiveCheckin.id, rootId, true)
    },
    async buildSubtree(checkinId, parentId, isRoot = false) {
      const tree = await galleryTreeService.getGalleryTree(checkinId, parentId).catch(() => null)
      if (!tree) return []
      const subs = Array.isArray(tree.sub_gallery) ? tree.sub_gallery : []
      const built = await Promise.all(subs.map(async g => {
        if (!g || g.id == null) return null
        const node = {id: g.id, name: g.name, count: g.num_media, children: null}
        if (g.can_expand) {
          const children = await this.buildSubtree(checkinId, g.id)
          if (children.length > 0) {
            node.children = children
            return node
          }
        }
        return g.num_media > 0 ? node : null
      }))
      const filtered = built.filter(n => n !== null)
      if (isRoot && filtered.length === 0 && tree.id != null && tree.num_media > 0) {
        return [{id: tree.id, name: tree.name, count: tree.num_media, children: null}]
      }
      return filtered
    },
    async fetchFlatGalleries() {
      const response = await mediaService.getGalleries(this.effectiveCheckin.id)
      const group = response && response[galleryTypes.UNLOCKED]
      if (!Array.isArray(group)) return []
      const nodes = []
      group.forEach(g => {
        if (g && g.media_count > 0) {
          nodes.push({
            id: g.id,
            name: g.name || this.$t('pages.checkins_detail.default_gallery'),
            count: g.media_count,
            children: null,
          })
        }
      })
      return nodes
    },
    downloadEmail() {
      this.loading = true
      const selection = this.showCheckboxList ? [...this.selectedGalleryIds] : this.galleryId
      EventService.downloadAlbum(this.effectiveCheckin.id, selection)
          .then(() => {
            this.$bvToast.toast(
                this.$t('toast_download_album.text'),
                {
                  title: this.$t('toast_download_album.title'),
                  headerClass: 'd-block pt-2',
                  noCloseButton: false,
                  autoHideDelay: 5000
                })
            this.$bvModal.hide('bv-modal-download-album-choice')
          })
          .catch(error => {
            if (error.response && error.response.status === 400 &&
                (error.response.data.code === 'bulk_download_not_available' || error.response.data.code === 'bulk_download_missing_hd')) {
              this.$bvToast.toast(
                  this.$t('toast_download_album.error'),
                  {variant: 'warning', autoHideDelay: 8000, html: true})
            }
            this.$bvModal.hide('bv-modal-download-album-choice')
          }).finally(() => this.loading = false)
    },
  },
}
</script>
